import { AppBar, Box, Breadcrumbs, Toolbar, Typography, useMediaQuery, useTheme, Button } from '@mui/material'
import { NavigateNext as NavigateNextIcon, Login as LoginIcon, Launch as LaunchIcon } from '@mui/icons-material'
import { OrganizationSwitcher, UserButton, SignInButton } from '@clerk/clerk-react'
import { Link, useLocation } from 'react-router-dom'
import { dark } from '@clerk/themes'
import erdosMillerLogoWhite from '@/assets/images/ErdosMiller_LogoLong-White.svg'
import { ThemeToggle } from '@/components/common/ThemeToggle'

interface HeaderProps {
  projectName?: string
  isSignedIn?: boolean
}

export function Header({ projectName, isSignedIn = false }: HeaderProps) {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isOnProjectsPage = location.pathname === '/projects'
  const isInWorkspace = location.pathname.startsWith('/workspace')

  return (
    <AppBar 
      position="fixed" 
      elevation={1} 
      color={theme.palette.mode === 'dark' ? 'default' : 'inherit'}
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)', // Theme-aware background
        backdropFilter: 'blur(10px)',
        color: theme.palette.mode === 'dark' ? 'white' : 'black', // Explicit text color
      }}>
      <Toolbar sx={{ 
        height: { xs: '56px', sm: '64px' }, // Reduced height on mobile
        minHeight: { xs: '56px !important', sm: '64px !important' },
        py: 0,
        px: { xs: 1, sm: 2 } // Reduced padding on mobile
      }}>

        
        {/* Logo */}
        <Box sx={{ 
          height: { xs: '56px', sm: '64px' },
          width: { xs: '140px', sm: '180px' }, // Adjusted for smaller logo
          display: 'flex', 
          alignItems: 'center',
          py: 0.5,
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img 
            src={erdosMillerLogoWhite} 
            alt="Erdos Miller" 
            className="wave-logo-header"
            style={{
              filter: theme.palette.mode === 'light' ? 'invert(1)' : 'none'
            }}
          />
        </Box>
        
        <Box sx={{ 
          flexGrow: 1, 
          ml: { xs: 1, sm: 2 }, // Added spacing between logo and content
          minWidth: 0 // Allow flex item to shrink
        }}>
          {isOnProjectsPage ? (
            <Typography 
              variant="body1" 
              color="inherit"
              noWrap
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1rem' }, // Smaller, less intense
                fontWeight: 500 // Medium weight instead of bold
              }}
            >
              Projects
            </Typography>
          ) : isInWorkspace ? (
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                '& .MuiBreadcrumbs-separator': {
                  mx: { xs: 0.5, sm: 1 }, // Tighter separator spacing on mobile
                  color: theme.palette.mode === 'dark' ? 'white' : 'black', // Theme-aware separator
                }
              }}
            >
              <Link to="/projects" style={{ color: 'inherit', textDecoration: 'none' }}>
                <Typography 
                  variant="body1"
                  color="inherit"
                  sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.95rem' }, // Smaller, less intense
                    fontWeight: 500, // Medium weight instead of bold
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Projects
                </Typography>
              </Link>
              <Typography 
                variant="body1" 
                color="inherit"
                noWrap
                sx={{ 
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: 500
                }}
              >
                {projectName || 'Workspace'}
              </Typography>
            </Breadcrumbs>
          ) : (
            <Typography 
              variant="body1" 
              color="inherit"
              noWrap
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 500
              }}
            >
              Wave
            </Typography>
          )}
        </Box>
        
        {/* FeatureBase Links */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0.5, sm: 1 }, 
          mr: { xs: 1, sm: 2 },
          alignItems: 'center'
        }}>
          <Button
            href="https://emwave.featurebase.app/roadmap"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            endIcon={<LaunchIcon sx={{ fontSize: '0.8rem' }} />}
            sx={{
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.8)' 
                : 'rgba(0, 0, 0, 0.7)',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 400,
              textTransform: 'none',
              minWidth: 'auto',
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            {isMobile ? 'Roadmap' : 'Roadmap'}
          </Button>
          
          <Button
            href="https://emwave.featurebase.app/changelog"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            endIcon={<LaunchIcon sx={{ fontSize: '0.8rem' }} />}
            sx={{
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.8)' 
                : 'rgba(0, 0, 0, 0.7)',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 400,
              textTransform: 'none',
              minWidth: 'auto',
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            {isMobile ? 'Changelog' : 'Changelog'}
          </Button>
        </Box>
        
        {/* Theme Toggle */}
        <Box sx={{ mr: { xs: 1, sm: 2 } }}>
          <ThemeToggle size="medium" />
        </Box>
        
        {isSignedIn ? (
          <>
            {/* Organization Switcher */}
            {!isMobile && (
              <Box sx={{ mr: 2 }}>
                <OrganizationSwitcher
                  appearance={{
                    baseTheme: theme.palette.mode === 'dark' ? dark : undefined
                  }}
                  createOrganizationMode="modal"
                  afterCreateOrganizationUrl="/"
                  afterSelectOrganizationUrl="/"
                  hidePersonal={false}
                />
              </Box>
            )}
            
            {/* User Button */}
            <UserButton 
              appearance={{
                baseTheme: theme.palette.mode === 'dark' ? dark : undefined
              }}
            />
          </>
        ) : (
          /* Sign In Button for non-authenticated users */
          <SignInButton mode="modal">
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              sx={{
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              {isMobile ? 'Sign In' : 'Sign In'}
            </Button>
          </SignInButton>
        )}
      </Toolbar>
    </AppBar>
  )
} 